import { useEffect, useRef, useState } from "react";

import { ErrorAlert, errorCodeOf } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { Modal } from "../components/Modal";

import { renderFrameCanvas } from "./framePdf";
import { encodeFrameForGallery } from "./galleryImage";

import type { ExcalidrawFrameLikeElement } from "@excalidraw/excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { ApiErrorCode, CanvaschoolApi } from "../api/client";
import type { FramePdfSource } from "./framePdf";

type GalleryApi = Pick<
  CanvaschoolApi,
  "publishGalleryPost" | "withdrawGalleryPost"
>;

export type GalleryPostState =
  | "PUBLISHED"
  | "WITHDRAWN"
  | "REMOVED_BY_TEACHER"
  | "HIDDEN"
  | "DELETED";

const PREVIEW_PX = 480;

export type GalleryShowoffControlProps = {
  api: GalleryApi;
  classId: string;
  canvasId: string;
  excalidrawAPI: ExcalidrawImperativeAPI;
  frame: ExcalidrawFrameLikeElement;
  /** Null when the frame was never posted. */
  status: GalleryPostState | null;
  postId: string | null;
  /** Called after a publish or withdraw so the caller re-reads the states. */
  onChanged(): void;
};

const readSource = (api: ExcalidrawImperativeAPI): FramePdfSource => ({
  elements: api.getSceneElements(),
  appState: api.getAppState(),
  files: api.getFiles(),
});

/** Keys go to whoever holds focus; a canvas still holding it would take the
 * arrows and Delete aimed at the dialog. */
const releaseCanvasFocus = () => {
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }
};

/**
 * 「학교 밖 자랑하기」 for one sealed frame of the student's own. The post
 * state comes from the caller (read once per canvas and after each action);
 * this component only asks, renders the picture and calls the API.
 */
export const GalleryShowoffControl = ({
  api,
  classId,
  canvasId,
  excalidrawAPI,
  frame,
  status,
  postId,
  onChanged,
}: GalleryShowoffControlProps) => {
  const [dialog, setDialog] = useState<"publish" | "withdraw" | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [posted, setPosted] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (dialog !== "publish") {
      return;
    }
    let cancelled = false;
    setPreview(null);
    void renderFrameCanvas(readSource(excalidrawAPI), frame, PREVIEW_PX)
      .then((canvas) => {
        if (!cancelled) {
          setPreview(canvas.toDataURL("image/png"));
        }
      })
      .catch(() => {
        // The preview is a courtesy; publishing renders on its own.
      });
    return () => {
      cancelled = true;
    };
  }, [dialog, excalidrawAPI, frame]);

  const open = (kind: "publish" | "withdraw") => {
    releaseCanvasFocus();
    setError(null);
    setRenderFailed(false);
    setPosted(false);
    setDialog(kind);
  };
  const close = () => {
    if (!pending) {
      setDialog(null);
    }
  };

  const publish = async () => {
    setPending(true);
    setError(null);
    setRenderFailed(false);
    try {
      let image: Blob;
      try {
        image = await encodeFrameForGallery(readSource(excalidrawAPI), frame);
      } catch {
        if (mounted.current) {
          setRenderFailed(true);
        }
        return;
      }
      await api.publishGalleryPost(classId, canvasId, frame.id, image);
      if (mounted.current) {
        setDialog(null);
        setPosted(true);
      }
      onChanged();
    } catch (caught) {
      if (mounted.current) {
        setError(errorCodeOf(caught));
      }
    } finally {
      if (mounted.current) {
        setPending(false);
      }
    }
  };

  const withdraw = async () => {
    if (!postId) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      await api.withdrawGalleryPost(postId);
      if (mounted.current) {
        setDialog(null);
        setPosted(false);
      }
      onChanged();
    } catch (caught) {
      if (mounted.current) {
        setError(errorCodeOf(caught));
      }
    } finally {
      if (mounted.current) {
        setPending(false);
      }
    }
  };

  const button = (label: string, onClick?: () => void) => (
    <button
      type="button"
      className="canvaschool-comment-button"
      disabled={!onClick}
      onClick={onClick}
    >
      {label}
    </button>
  );

  let body;
  if (status === "PUBLISHED") {
    body = (
      <>
        <span className="canvaschool-frame-seal-label">갤러리에 있어요</span>
        {button("다시 올리기", () => open("publish"))}
        {button("내리기", () => open("withdraw"))}
      </>
    );
  } else if (status === "REMOVED_BY_TEACHER") {
    body = button("선생님이 내렸어요");
  } else if (status === "HIDDEN") {
    body = button("확인 중이에요");
  } else if (status === "DELETED") {
    body = button("올릴 수 없어요");
  } else {
    body = button("학교 밖 자랑하기", () => open("publish"));
  }

  return (
    <>
      {body}
      {posted && (
        <span role="status">
          갤러리에 올렸어요 <Link to="/gallery">갤러리 보러 가기</Link>
        </span>
      )}
      {dialog === "publish" && (
        <Modal
          title="학교 밖 자랑하기"
          className="canvaschool-modal--wide"
          primaryLabel={pending ? "올리는 중…" : "올리기"}
          pending={pending}
          onPrimary={() => void publish()}
          onCancel={close}
        >
          {preview && (
            <img
              className="canvaschool-gallery-preview"
              alt="올릴 그림 미리보기"
              src={preview}
            />
          )}
          <p className="canvaschool-modal-lead">
            학교와 학급 이름과 함께 전국의 크루바스 친구·선생님에게 보여요.
            이름이나 얼굴, 사는 곳이 그림에 있으면 올리지 마세요.
          </p>
          {error && <ErrorAlert code={error} />}
          {renderFailed && (
            <p className="canvaschool-error" role="alert">
              그림을 만들지 못했어요. 다시 시도해 주세요.
            </p>
          )}
        </Modal>
      )}
      {dialog === "withdraw" && (
        <Modal
          title="갤러리에서 내리기"
          primaryLabel="내리기"
          danger
          pending={pending}
          onPrimary={() => void withdraw()}
          onCancel={close}
        >
          <p className="canvaschool-modal-lead">
            갤러리에서 내릴까요? 다시 올릴 수 있어요.
          </p>
          {error && <ErrorAlert code={error} />}
        </Modal>
      )}
    </>
  );
};
